import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { extractUniqueBadges } from "../utils/productNameBadges";
import type { WholesaleForecastData } from "./ForecastAccountsCell";
import ProductRow from "./ProductRow";
import ProductContextRow, { ContextData } from "./ProductContextRow";
import PlanNodeHeaderRow from "./PlanNodeHeaderRow";
import MonthRevenueHeaderCell from "./MonthRevenueHeaderCell";
import MonthMarginHeaderRow from "./MonthMarginHeaderRow";
import { channelMargin } from "../utils/channelMargin";
import { useEditableGridColumn } from "../utils/useEditableGridColumn";
import type { CellStatus } from "../utils/useSaveStatus";
import { PLAN_PRODUCT_COL_WIDTH } from "@/lib/gridLayout";

interface MonthCell {
  period_date: string;
  quantity: number;
  average_selling_price: number;
  actual_quantity: number | null;
  actual_revenue?: number | null;
  actual_cogs?: number | null;
  actual_gm: number | null;
}

interface AspCell {
  price: number;
  currency: string;
}

interface Row {
  bottom_up_product_id: string;
  product_external_id: string;
  product_name: string;
  product_short_name: string | null;
  product_sku: string | null;
  plan_node_id: string;
  asp: AspCell | null;
  cogs: number | null;
  months: MonthCell[];
}

interface PlanNode {
  id: string;
  name: string;
  ancestor_names?: string[];
  target_revenue?: number;
  past_revenue?: number;
}

interface Props {
  months: string[];
  rows: Row[];
  planCurrency?: string | null;
  planNodes: PlanNode[];
  editable: boolean;
  canViewGrossMargin: boolean;
  // First ISO date (YYYY-MM-01) that is still editable — the later of the
  // current month and a revision version's plan period start. Months before
  // it are actual sales and read-only.
  lockedBefore: string;
  // "Oct – Dec 2026" for a revision's plan period, shown next to each node's
  // target so the number is understood as remaining-months only. Null for a
  // plain version.
  targetPeriodLabel?: string | null;
  onCellChange: (productExternalId: string, periodDate: string, quantity: number) => void;
  onPasteCells: (
    productExternalId: string,
    updates: { period_date: string; quantity: number }[],
  ) => void;
  saveStatusFor?: (productExternalId: string, periodDate: string) => CellStatus | undefined;
  wholesaleForecast?: WholesaleForecastData | null;
  // Per-product expandable context (past actual sales). The
  // fetch targets the ByMonth context endpoint, so pages without it opt out.
  contextRows?: boolean;
  // Plan-vs-Actual mode: unit-attainment hairlines in cells, actual revenue
  // lines in node headers and grand totals.
  showActuals?: boolean;
  // Vertical hairlines between month columns. Defaults to showActuals.
  monthDividers?: boolean;
  // Height (px) of content stuck above the grid header in the same scroll
  // pane (e.g. StickyBar toolbar); header rows offset their sticky top by it.
  stickyOffset?: number;
  // GM% header row under each month's revenue, from actual sales: past
  // months only, the current and future months stay blank. Still hidden when
  // the viewer may not see gross margin.
  showMonthMargin?: boolean;
}

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function shortMonthLabel(iso: string): string {
  const [, m] = iso.split("-");
  return MONTH_LABELS[parseInt(m, 10) - 1] ?? iso;
}

export default function BottomUpGrid({
  months,
  rows,
  planCurrency,
  planNodes,
  editable,
  canViewGrossMargin,
  lockedBefore,
  targetPeriodLabel,
  onCellChange,
  onPasteCells,
  saveStatusFor,
  wholesaleForecast,
  contextRows = true,
  showActuals = false,
  monthDividers = showActuals,
  stickyOffset = 0,
  showMonthMargin = false,
}: Props) {
  // ISO YYYY-MM-DD strings compare correctly as strings.
  const isPastMonth = useCallback((iso: string) => iso < lockedBefore, [lockedBefore]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [contextCache, setContextCache] = useState<Map<string, ContextData>>(new Map());
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());

  // Header rows stick to the layout's scroll pane; the page body scrolls as
  // one, so the grid no longer owns a bounded scroll container.
  const headRow1Ref = useRef<HTMLTableRowElement>(null);
  const headRow2Ref = useRef<HTMLTableRowElement>(null);
  const theadRef = useRef<HTMLTableSectionElement>(null);
  const [row2Top, setRow2Top] = useState(0);
  const [row3Top, setRow3Top] = useState(0);
  const [theadHeight, setTheadHeight] = useState(0);

  const measureHead = () => {
    if (headRow1Ref.current) {
      // Exact fractional height: stuck position must equal natural flow
      // position, or the header visibly shifts the moment scrolling starts.
      const row1Height = headRow1Ref.current.getBoundingClientRect().height;
      setRow2Top(row1Height);
      setRow3Top(row1Height + (headRow2Ref.current?.getBoundingClientRect().height ?? 0));
    }
    if (theadRef.current) {
      setTheadHeight(theadRef.current.getBoundingClientRect().height);
    }
  };
  useLayoutEffect(() => {
    measureHead();
    const el = headRow1Ref.current;
    if (!el) return;
    // ResizeObserver also catches height changes no resize event fires for
    // (late web-font load, zoom).
    const observer = new ResizeObserver(measureHead);
    observer.observe(el);
    if (theadRef.current) observer.observe(theadRef.current);
    return () => observer.disconnect();
  }, []);

  const headerTop = stickyOffset;
  useEffect(() => {
    measureHead();
  });

  const toggleExpand = useCallback(
    async (bottomUpProductId: string) => {
      setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(bottomUpProductId)) next.delete(bottomUpProductId);
        else next.add(bottomUpProductId);
        return next;
      });

      if (contextCache.has(bottomUpProductId) || loadingIds.has(bottomUpProductId)) return;
      setLoadingIds((prev) => new Set(prev).add(bottomUpProductId));
      try {
        const url = `${window.location.pathname}/context?bottom_up_product_id=${encodeURIComponent(bottomUpProductId)}`;
        const res = await fetch(url, { headers: { Accept: "application/json" } });
        if (res.ok) {
          const json: ContextData = await res.json();
          setContextCache((prev) => {
            const next = new Map(prev);
            next.set(bottomUpProductId, json);
            return next;
          });
        }
      } finally {
        setLoadingIds((prev) => {
          const next = new Set(prev);
          next.delete(bottomUpProductId);
          return next;
        });
      }
    },
    [contextCache, loadingIds],
  );

  const monthRevenues = useMemo(() => {
    const totals = new Map<string, number>();
    for (const m of months) totals.set(m, 0);
    for (const r of rows) {
      const price = r.asp?.price ?? 0;
      if (!price) continue;
      for (const c of r.months) {
        const qty = c.quantity;
        totals.set(c.period_date, (totals.get(c.period_date) ?? 0) + (qty || 0) * price);
      }
    }
    return totals;
  }, [months, rows]);

  // Real revenue from sales, summed independently of plan/ASP so the two
  // totals never mix sources.
  const actualMonthRevenues = useMemo(() => {
    const totals = new Map<string, number>();
    if (!showActuals) return totals;
    for (const r of rows) {
      for (const c of r.months) {
        if (c.actual_revenue == null) continue;
        totals.set(c.period_date, (totals.get(c.period_date) ?? 0) + c.actual_revenue);
      }
    }
    return totals;
  }, [rows, showActuals]);

  // Same basis as the channel GM% summary: actual sales, past months only.
  const monthMargins = useMemo(
    () => (showMonthMargin && canViewGrossMargin ? channelMargin(rows, lockedBefore).byMonth : null),
    [showMonthMargin, canViewGrossMargin, rows, lockedBefore],
  );

  const grouped = useMemo(() => {
    const rowsByNode = new Map<string, Row[]>();
    for (const r of rows) {
      const arr = rowsByNode.get(r.plan_node_id) ?? [];
      arr.push(r);
      rowsByNode.set(r.plan_node_id, arr);
    }
    return planNodes.map((node) => {
      const list = rowsByNode.get(node.id) ?? [];
      return {
        id: node.id,
        name: node.name,
        ancestor_names: node.ancestor_names ?? [],
        target_revenue: node.target_revenue ?? 0,
        past_revenue: node.past_revenue ?? 0,
        rows: list,
        badges: extractUniqueBadges(list.map((r) => r.product_name)),
      };
    });
  }, [rows, planNodes]);

  // Flat row order (across plan-node groups) for rectangular selection indexing.
  const flatRows = useMemo(() => grouped.flatMap((g) => g.rows), [grouped]);
  const rowIndexById = useMemo(() => {
    const m = new Map<string, number>();
    flatRows.forEach((r, i) => m.set(r.bottom_up_product_id, i));
    return m;
  }, [flatRows]);

  const handlePasteQtyUpdates = useCallback(
    (productExternalId: string, updates: { period_date: string; value: number }[]) =>
      onPasteCells(productExternalId, updates.map((u) => ({ period_date: u.period_date, quantity: u.value }))),
    [onPasteCells],
  );
  const parseQtyInput = useCallback((raw: string) => parseInt(raw.replace(/[^\d]/g, ""), 10) || 0, []);
  const {
    isSelected,
    onCellMouseDown: handleCellMouseDown,
    preview: previewQty,
    setPreview: setPreviewQty,
    handleCellCommit,
  } = useEditableGridColumn(flatRows, months, isPastMonth, handlePasteQtyUpdates, onCellChange, parseQtyInput);

  const totalProductCount = rows.length;
  const colSpan = 1 + months.length;

  return (
    <>
      <div>
        {/* Opaque backdrop pinned behind the sticky toolbar + header rows.
            Zero layout footprint (negative margin cancels its height); z-20
            sits above scrolling body content (incl. z-10 expand chevrons) but
            below the sticky header cells (same z-20, later in DOM), so
            nothing shows through paint seams in the header stack. */}
        <div
          aria-hidden
          className="sticky z-20"
          style={{
            top: 0,
            height: stickyOffset + theadHeight,
            // Negative margins cancel the height AND align the backdrop's
            // natural position over the toolbar + thead (not the body rows).
            marginTop: -stickyOffset,
            marginBottom: -theadHeight,
            background: "var(--background)",
          }}
        />
        <table
          className="border-collapse text-xs"
          style={{ tableLayout: "fixed", width: "100%" }}
        >
          <thead ref={theadRef}>
            {/* No tr border-b in the sticky thead: collapsed borders live on
                the table's own layer and scroll away from stuck cells, leaving
                a see-through strip. Cell box-shadows paint the separators
                instead — they move with the sticky cells. */}
            <tr ref={headRow1Ref}>
              <th
                style={{ background: "var(--muted)", color: "var(--muted-foreground)", borderColor: "var(--border)", top: headerTop, width: PLAN_PRODUCT_COL_WIDTH, maxWidth: PLAN_PRODUCT_COL_WIDTH }}
                className="sticky left-0 top-0 z-30 text-left px-3 py-2 font-semibold whitespace-nowrap border-r"
              >
                Product
              </th>
              {months.map((m) => {
                const past = isPastMonth(m);
                return (
                  <th
                    key={m}
                    style={{
                      background: past ? "var(--border)" : "var(--muted)",
                      color: "var(--muted-foreground)",
                      top: headerTop,
                      ...(monthDividers ? { borderColor: "var(--accent)" } : {}),
                    }}
                    className={`sticky top-0 z-20 px-2 py-2 text-right font-semibold whitespace-nowrap${monthDividers ? " border-r" : ""}`}
                  >
                    {shortMonthLabel(m)}
                  </th>
                );
              })}
            </tr>
            <tr ref={headRow2Ref} style={{ background: "var(--muted)" }}>
              <th
                // Row 2 paints after row 1, so it draws both header separator
                // lines: -1px = row1/row2 boundary, +1px = header/body boundary.
                style={{ background: "var(--muted)", color: "var(--foreground)", borderColor: "var(--border)", boxShadow: "0 -1px 0 0 var(--border), 0 1px 0 0 var(--border)", top: headerTop + row2Top, width: PLAN_PRODUCT_COL_WIDTH, maxWidth: PLAN_PRODUCT_COL_WIDTH }}
                className="sticky left-0 z-30 text-left px-3 py-2 font-semibold whitespace-nowrap border-r"
              >
                Revenue
              </th>
              {months.map((m) => (
                <MonthRevenueHeaderCell
                  key={m}
                  past={isPastMonth(m)}
                  plan={monthRevenues.get(m) ?? 0}
                  actual={actualMonthRevenues.get(m) ?? 0}
                  showActuals={showActuals}
                  monthDividers={monthDividers}
                  top={headerTop + row2Top}
                  planCurrency={planCurrency}
                />
              ))}
            </tr>
            {monthMargins && (
              <MonthMarginHeaderRow
                months={months}
                margins={monthMargins}
                isPastMonth={isPastMonth}
                monthDividers={monthDividers}
                top={headerTop + row3Top}
              />
            )}
          </thead>

          <tbody>
            {grouped.map((group) => (
              <Fragment key={group.id}>
                <PlanNodeHeaderRow group={group} months={months} monthTotals={monthRevenues} isPastMonth={isPastMonth} planCurrency={planCurrency} showActuals={showActuals} monthDividers={monthDividers} targetPeriodLabel={targetPeriodLabel} />

                {group.rows.length === 0 && (
                  <tr style={{ borderColor: "var(--accent)" }} className="border-b">
                    <td
                      colSpan={colSpan}
                      style={{ background: "var(--background)", color: "var(--muted-foreground)" }}
                      className="px-3 py-3 text-xs italic"
                    >
                      No products yet.
                    </td>
                  </tr>
                )}
                {group.rows.map((r) => {
                  const isExpanded = expanded.has(r.bottom_up_product_id);
                  return (
                    <Fragment key={r.bottom_up_product_id}>
                      <ProductRow
                        row={r}
                        rowIndex={rowIndexById.get(r.bottom_up_product_id) ?? 0}
                        displayName={r.product_short_name || group.badges.get(r.product_name) || r.product_name}
                        editable={editable}
                        canViewGrossMargin={canViewGrossMargin}
                        planCurrency={planCurrency}
                        isPastMonth={isPastMonth}
                        onCellChange={handleCellCommit}
                        onPasteCells={onPasteCells}
                        saveStatusFor={saveStatusFor}
                        isCellSelected={isSelected}
                        onCellMouseDown={handleCellMouseDown}
                        preview={previewQty}
                        onPreview={setPreviewQty}
                        isExpanded={isExpanded}
                        onToggleExpand={contextRows ? () => toggleExpand(r.bottom_up_product_id) : undefined}
                        monthCount={months.length}
                        forecastMonths={wholesaleForecast?.products[r.product_external_id]}
                        forecastCustomers={wholesaleForecast?.customers}
                        showActuals={showActuals}
                        monthDividers={monthDividers}
                      />
                      {contextRows && isExpanded && (
                        <ProductContextRow
                          months={months}
                          data={contextCache.get(r.bottom_up_product_id) ?? null}
                          loading={loadingIds.has(r.bottom_up_product_id)}
                        />
                      )}
                    </Fragment>
                  );
                })}
              </Fragment>
            ))}

            {grouped.length === 0 && (
              <tr>
                <td
                  colSpan={colSpan}
                  style={{ color: "var(--chart-muted)" }}
                  className="px-4 py-8 text-center text-sm"
                >
                  No plan nodes found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
